import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Menu, X, ChevronLeft, ChevronRight, LogOut,
  ListFilter, Network, ShipWheel, LayoutDashboard,
  ArrowLeftRight, Moon, Sun, Shield, GraduationCap
} from 'lucide-react';

const TABS = [
  { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard /> },
  { path: '/screeners', label: 'Screeners', icon: <ListFilter /> },
  { path: '/cluster', label: 'Cluster', icon: <Network /> },
  { path: '/helms', label: 'Helm', icon: <ShipWheel /> },
  { path: '/live', label: 'Trading', icon: <ArrowLeftRight /> },
  { path: '/university', label: 'University', icon: <GraduationCap /> },
];

export default function AppLayout({ children, backTo, backLabel, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // One superadmin entry for the whole admin space; its sections (analytics,
  // users) are tabs inside it. `activeTab` matches on prefix, so any
  // /admin/* path keeps this highlighted.
  const tabs = user?.is_superadmin
    ? [...TABS, { path: '/admin', label: 'Admin', icon: <Shield /> }]
    : TABS;

  const [isDarkActive, setIsDarkActive] = useState(
    localStorage.getItem('theme') === 'dark' || 
    (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );
  
  const [isCollapsed, setIsCollapsed] = useState(
    localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleCollapsed = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  const activeTab = tabs.slice().reverse().find(t => location.pathname.startsWith(t.path))?.path || '/';

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    const html = document.documentElement;
    if (html.classList.contains('dark')) {
      html.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDarkActive(false);
    } else {
      html.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDarkActive(true);
    }
  };

  // Determine if the top header actually has content to show
  const hasHeaderContent = title || backTo;

  return (
    <div className="min-h-screen bg-background flex text-foreground">
      
      {/* MOBILE OVERLAY */}
      {isMobileOpen && (
        <div>
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-xs z-40 md:hidden"
            onClick={() => setIsMobileOpen(false)}
          />
        </div>
      )}

      {/* SIDEBAR */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-screen bg-muted/20 rounded-lg shadow-lg transition-all duration-300 flex flex-col
          ${isCollapsed ? 'w-18' : 'w-64'} 
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Sidebar Header / Logo (CORRECTED) */}
        <div className="h-12 flex items-center justify-between px-3 border-border shrink-0">
          <div className="flex items-center gap-2 overflow-hidden">
            {!isCollapsed && <div className="logo-container shrink-0 flex items-center">
              <img 
                src={process.env.PUBLIC_URL + (isDarkActive ? '/jigri-logo-dark.png': '/jigri-logo1.png')} 
                alt="Jigri Logo" 
                className="main-logo h-7 w-auto object-contain rounded-md"
              />
            </div>}
            {isCollapsed && <span className="font-bold text-base tracking-wide truncate">JIGRI</span>}
          </div>
          
          <button 
            className="md:hidden p-1.5 text-muted-foreground hover:text-foreground rounded-md"
            onClick={() => setIsMobileOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto overflow-x-hidden">
          {tabs.map(tab => (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.path 
                  ? 'bg-primary/10 text-primary' 
                  : 'text-muted-foreground hover:bg-surface-hover hover:text-foreground'
              }`}
              title={isCollapsed ? tab.label : undefined}
            >
              <span className="w-5 h-5 shrink-0 flex items-center justify-center">{tab.icon}</span>
              {(isMobileOpen || !isCollapsed) && <span className="truncate">{tab.label}</span>}
            </button>
          ))}
        </nav>

        {/* Sidebar Footer: Theme, User, Collapse Toggle */}
        <div className="p-2 space-y-1 shrink-0">
          <button
            onClick={toggleTheme}
            className={`w-full flex items-center gap-3 px-3 py-2.5 mt-2 rounded-md text-sm font-medium text-muted-foreground bg-surface-hover/50 hover:bg-surface-hover hover:text-foreground transition-colors ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? "Toggle Theme" : undefined}
          >
            <span className="w-5 h-5 shrink-0 flex items-center justify-center">
              {isDarkActive ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </span>
            {!isCollapsed && <span>{isDarkActive ? 'Dark Mode' : 'Light Mode'}</span>}
          </button>

          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} px-3 py-2.5 mt-2 bg-surface-hover/50 rounded-md`}>
            {!isCollapsed && (
              <span className="text-sm font-medium truncate pr-2">
                {user?.name || 'My Account'}
              </span>
            )}
            <button 
              onClick={logout} 
              className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={toggleCollapsed}
            className={`hidden md:flex w-full items-center py-2 mt-2 px-3 text-muted-foreground hover:text-foreground transition-colors ${isCollapsed ? 'justify-center' : 'justify-end'}`}
          >
            {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div 
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          isCollapsed ? 'md:ml-16' : 'md:ml-64'
        }`}
      >
        {/* Top Header (CORRECTED: Auto-hides on desktop if empty) */}
        <header className={`sticky top-0 z-30 flex items-center px-4 gap-3 bg-surface-base/80 backdrop-blur-md transition-all overflow-hidden
          ${hasHeaderContent 
            ? 'h-12 border-b border-border' 
            : 'h-12 border-b border-border md:h-0 md:border-b-0' 
          }
        `}>
          {/* Mobile Hamburger (Always visible on mobile) */}
          <button 
            className="md:hidden p-1.5 -ml-1.5 text-muted-foreground hover:text-foreground rounded-md shrink-0"
            onClick={() => setIsMobileOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <img 
            src={process.env.PUBLIC_URL + (isDarkActive ? '/jigri-logo-dark.png': '/jigri-logo1.png')} 
            alt="Jigri Logo" 
            className="md:hidden main-logo h-7 w-auto object-contain rounded-md"
          />

          {/* Breadcrumbs & Title */}
          <div className="flex items-center gap-2 min-w-0">
            {backTo && (
              <>
                <button 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0" 
                  onClick={() => navigate(backTo)}
                >
                  {backLabel || '← Back'}
                </button>
                <span className="text-muted-foreground hidden sm:block">/</span>
              </>
            )}
            {title && (
              <span className="text-sm font-medium text-foreground truncate">
                {title}
              </span>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden p-4">
          {children}
        </main>
      </div>

    </div>
  );
}